import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Typography, Tabs, Tab, Grid,
  CircularProgress, IconButton, Chip, Alert,
  FormControlLabel, Checkbox, Autocomplete, Paper, Tooltip,
} from '@mui/material';
import {
  Close, CloudUpload, Delete, Star, StarBorder, AddPhotoAlternate,
  Visibility, VisibilityOff, Person, Description, School, Work,
  People, Phone, Favorite, FavoriteBorder, LocationOn, ManageAccounts, PhotoCamera,
  AutoAwesome,
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import adminService from '../../services/adminService';
import stateDistrictData from '../../libs/state&dist.json';

// ─── Constants ───────────────────────────────────────────────────────────────

const GENDERS = ['Male', 'Female', 'Other'];
const PROFILE_FOR = ['Self', 'Son', 'Daughter', 'Brother', 'Sister', 'Relative', 'Friend'];
const LOOKING_FOR = ['Marriage', 'Companionship'];
const MARITAL_STATUS = ['Never Married', 'Divorced', 'Widowed', 'Awaiting Divorce', 'Annulled', 'Not Specified'];
const MEMBERSHIP_TYPES = ['Free', 'Premium'];
const DIET = ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Jain'];
const DRINKING_SMOKING = ['Yes', 'No', 'Occasionally'];
const EMPLOYED_IN = ['Private Sector', 'Government/Public Sector', 'Civil Services', 'Defence', 'Business/Self Employed', 'Not Working'];
const SETTLING_ABROAD = ['Yes', 'No', 'Undecided'];
const PROFILE_MANAGED_BY = ['Self', 'Parent', 'Sibling', 'Relative/Friend', 'Marriage Bureau', 'Other'];
const DISABILITY = ['None', 'Physically disabled from birth', 'Physically disabled due to accident', 'Mentally disabled from birth', 'Mentally disabled due to accident', 'Thalassemia', 'HIV+'];
const THALASSEMIA = ['No', 'Major', 'Minor'];
const FAMILY_STATUS = ['Rich/Affluent', 'Upper Middle Class', 'Middle Class'];
const FAMILY_TYPE = ['Joint Family', 'Nuclear Family', 'Extended Family', 'Others'];
const FAMILY_VALUES = ['Orthodox', 'Conservative', 'Moderate', 'Liberal', 'Traditional'];
const LIVING_WITH_PARENTS = ['Yes', 'No', 'Not Applicable'];
const CITIZENSHIP = ['Citizen', 'Permanent Resident', 'Work Permit', 'Student Visa', 'Other'];
const MANGLIK = ['Yes', 'No', 'Anshik', "Don't Know"];
const PHONE_OWNED_BY = ['Self', 'Parent', 'Sibling', 'Relative'];
const INCOME_PERIOD = ['Monthly', 'Yearly'];
const VISIBILITY = ['all', 'paid-only'];
const BROTHER_SISTER_COUNT = [0, 1, 2, 3, 4, 5];
const HEIGHT_OPTIONS = Array.from({ length: 61 }, (_, i) => i + 140);
const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Parsi', 'Jewish', 'Other'];
const CASTE_BY_RELIGION = {
  Hindu: ['Brahmin', 'Kshatriya', 'Vaishya', 'Shudra', 'Kayastha', 'Khatri', 'Maratha', 'Jat', 'Yadav', 'Rajput', 'Other'],
  Muslim: ['Sunni', 'Shia', 'Ahmadiyya', 'Other'],
  Christian: ['Catholic', 'Protestant', 'Orthodox', 'Other'],
  Sikh: ['Jat', 'Khatri', 'Ramgarhia', 'Other'],
  Buddhist: ['Mahayana', 'Theravada', 'Vajrayana', 'Other'],
  Jain: ['Digambar', 'Shwetambar', 'Other'],
  Parsi: ['Parsi', 'Irani', 'Other'],
  Jewish: ['Ashkenazi', 'Sephardic', 'Other'],
  Other: ['Other'],
};
const MOTHER_TONGUES = [
  'Hindi', 'Bengali', 'Telugu', 'Marathi', 'Tamil', 'Urdu', 'Gujarati',
  'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Assamese', 'Maithili',
  'Kashmiri', 'Konkani', 'Sindhi', 'Sanskrit', 'English', 'Other',
];
const DRESS_STYLES = [
  'Traditional', 'Western', 'Indo-Western', 'Casual', 'Formal',
  'Sporty', 'Elegant', 'Trendy', 'Vintage', 'Bohemian', 'Minimalist', 'Modest', 'Chic',
];
const HOBBIES_OPTIONS = [
  'Reading', 'Writing', 'Cooking', 'Baking', 'Gardening', 'Photography', 'Painting',
  'Drawing', 'Singing', 'Dancing', 'Playing musical instruments', 'Hiking', 'Camping',
  'Traveling', 'Watching movies', 'Watching TV shows', 'Playing video games',
  'Meditation', 'Yoga', 'Gym', 'Running', 'Swimming', 'Cycling', 'Martial arts',
];
const LANGUAGE_OPTIONS = [
  'Hindi', 'English', 'Bengali', 'Telugu', 'Marathi', 'Tamil', 'Urdu', 'Gujarati',
  'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Assamese', 'French', 'German', 'Spanish',
  'Chinese', 'Japanese', 'Korean', 'Arabic', 'Russian', 'Portuguese', 'Italian',
];
const CUISINE_OPTIONS = [
  'North Indian', 'South Indian', 'Chinese', 'Italian', 'Mexican', 'Thai',
  'Japanese', 'Korean', 'Continental', 'Mughlai', 'Bengali', 'Gujarati',
  'Rajasthani', 'Punjabi', 'Maharashtrian', 'Goan', 'Hyderabadi', 'Chettinad',
];
const MUSIC_OPTIONS = [
  'Bollywood', 'Classical', 'Folk', 'Rock', 'Pop', 'Jazz', 'Blues', 'Hip Hop',
  'Electronic', 'Ghazal', 'Sufi', 'Devotional', 'Instrumental', 'Indian Classical', 'Western Classical',
];
const SPORTS_OPTIONS = [
  'Cricket', 'Football', 'Basketball', 'Tennis', 'Badminton', 'Table Tennis',
  'Hockey', 'Volleyball', 'Swimming', 'Athletics', 'Chess', 'Golf', 'Cycling', 'Yoga', 'Gym', 'Kabaddi',
];
const DEGREE_OPTIONS = ["High School", "Diploma", "Bachelor's", "Master's", "Ph.D.", "Trade School", "Other"];
const COUNTRIES = ['India', 'USA', 'UK', 'Canada', 'Australia', 'UAE', 'Singapore', 'New Zealand', 'Germany', 'France', 'Italy', 'Malaysia', 'Saudi Arabia', 'Qatar', 'Other'];
const INDIAN_STATES = stateDistrictData.states.map(({ state }) => state);
const DISTRICTS_BY_STATE = stateDistrictData.states.reduce((districtMap, { state, districts }) => {
  districtMap[state] = districts;
  return districtMap;
}, {});
const CITIES_BY_COUNTRY = {
  USA: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Other'],
  UK: ['London', 'Birmingham', 'Manchester', 'Glasgow', 'Other'],
  Canada: ['Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Other'],
  Australia: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Other'],
};

// Partner Preferences options — mirrors the fields actually captured by the
// Client app's registration (PartnerPreferences.jsx) and edit-profile
// (PartnerPreferencesEdit.jsx) flows, so admin edits stay consistent with
// what members themselves can set.
const PARTNER_MARITAL_STATUS = ['Never Married', 'Divorced', 'Widowed', 'Awaiting Divorce', 'Annulled'];
const PARTNER_EDUCATION_LEVELS = ["High School", "Diploma", "Bachelor's", "Master's", "Doctorate", "Professional Degree", "Any"];
const PARTNER_OCCUPATIONS = [
  'Software Professional', 'Engineer', 'Doctor', 'Teacher', 'Professor', 'Lawyer',
  'Businessman', 'Entrepreneur', 'Chartered Accountant', 'Financial Analyst', 'Bank Employee',
  'Government Employee', 'Defense Employee', 'Architect', 'Designer', 'Consultant',
  'Marketing Professional', 'Sales Professional', 'Artist', 'Scientist', 'Researcher',
  'Student', 'Self Employed', 'Any',
];
const PARTNER_DIET_OPTIONS = ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Jain', 'Vegan', 'Any'];
const PARTNER_DRINKING_OPTIONS = [
  { value: 'Any', label: "Doesn't Matter" },
  { value: 'NoDrinking', label: 'Non-drinker' },
  { value: 'Occasionally', label: 'Drinks Occasionally' },
];
const PARTNER_SMOKING_OPTIONS = [
  { value: 'Any', label: "Doesn't Matter" },
  { value: 'NoSmoking', label: 'Non-smoker' },
];
// Matches Client/src/components/profile/PartnerPreferencesEdit.jsx's indianStatesOptions
// exactly (distinct from the admin's own INDIAN_STATES, which is derived from
// state&dist.json and includes union territories members can't select as a preference).
const PARTNER_INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Other',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function SectionPaper({ title, description, children }) {
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      {title && (
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

function SaveButton({ onClick, saving, disabled }) {
  return (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        variant="contained"
        color="primary"
        onClick={onClick}
        disabled={saving || disabled}
        startIcon={saving ? <CircularProgress size={16} /> : null}
      >
        {saving ? 'Saving…' : 'Save Changes'}
      </Button>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const EditUserDialog = ({ open, userId, onClose, onSaved }) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);

  // User-level fields (Account tab)
  const [userData, setUserData] = useState({
    name: '', email: '', phone: '', gender: '', profileFor: '', lookingFor: 'Marriage',
    isEmailVerified: false, isVerified: false, membershipType: 'Free',
  });

  // Profile-level fields
  const [profileData, setProfileData] = useState({
    // Basic Details
    showNameToAll: true,
    dateOfBirth: '', maritalStatus: 'Not Specified',
    height: '', weight: '',
    profileManagedBy: 'Self',
    disability: 'None', thalassemia: 'No',
    income: { amount: '', currency: 'INR', period: 'Yearly' },
    gothra: '', currentLocation: '',
    visibility: 'all',
    // About Me
    aboutMe: '', hivPositive: false,
    religion: '', community: '', subCommunity: '', motherTongue: '',
    // Education
    education: { highestDegree: '', ugDegree: '', ugCollege: '', schoolName: '', aboutEducation: '' },
    // Career
    career: { employedIn: '', occupation: '', organizationName: '', interestedInSettlingAbroad: '', thoughtsOnSettlingAbroad: '' },
    occupation: '', employer: '',
    annualIncome: { amount: '', currency: 'INR', period: 'Yearly' },
    // Family
    familyDetails: {
      familyStatus: '', familyType: '', familyValues: '', livingWithParents: '',
      familyIncome: '', fatherOccupation: '', motherOccupation: '',
      brothers: { total: '', married: '' },
      sisters: { total: '', married: '' },
      familyBasedOutOf: '', aboutFamily: '',
    },
    // Contact
    contact: { email: '', alternateEmail: '', phoneNumber: '', phoneNumberOwnedBy: '', alternateNumber: '' },
    // Kundli
    kundliDetails: { manglikStatus: "Don't Know", nakshatra: '', timeOfBirth: '', placeOfBirth: '', thoughtsOnHoroscope: '' },
    // Lifestyle
    lifestyle: {
      diet: '', drinking: '', smoking: '',
      assets: { ownHouse: false, ownCar: false, description: '' },
      interests: {
        hobbies: [], languagesSpoken: [], foodICook: '', dressStyle: '',
        cuisinePreferences: [], favouriteMusic: [], sports: [],
        favouriteBooks: [], movies: [], tvShows: [], vacationDestination: [],
      },
    },
    // Location
    locationDetails: { country: 'India', state: '', city: '', citizenshipStatus: '' },
    // Partner Preferences — mirrors exactly what members can set via the
    // Client app's registration (PartnerPreferences.jsx) and edit-profile
    // (PartnerPreferencesEdit.jsx) forms, so admin edits stay consistent
    // with what's actually stored.
    partnerPreferences: {
      ageRange: { min: 18, max: 40 },
      heightRange: { min: 150, max: 190 },
      maritalStatus: [],
      religions: [],
      motherTongues: [],
      educationLevel: [],
      occupations: [],
      incomeRange: { min: '', max: '' },
      locationPreferences: { countries: [], states: [], cities: [] },
      dietPreferences: [],
      drinkingHabits: 'Any',
      smokingHabits: 'Any',
      aboutPartner: '',
    },
  });

  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (open && userId) loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const loadUser = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setNewPassword('');
    try {
      const res = await adminService.getUserDetails(userId);
      const { user, profile } = res.data.data;

      setUserData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        gender: user.gender || '',
        profileFor: user.profileFor || '',
        lookingFor: user.lookingFor || 'Marriage',
        isEmailVerified: user.isEmailVerified || false,
        isVerified: user.isVerified || false,
        membershipType: user.membershipType || 'Free',
      });

      if (profile) {
        setProfileData({
          showNameToAll: profile.showNameToAll !== false,
          dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.substring(0, 10) : '',
          maritalStatus: profile.maritalStatus || 'Not Specified',
          height: profile.height || '',
          weight: profile.weight || '',
          profileManagedBy: profile.profileManagedBy || 'Self',
          disability: profile.disability || 'None',
          thalassemia: profile.thalassemia || 'No',
          income: { amount: profile.income?.amount || '', currency: profile.income?.currency || 'INR', period: profile.income?.period || 'Yearly' },
          gothra: profile.gothra || '',
          currentLocation: profile.currentLocation || '',
          visibility: profile.visibility || 'all',
          aboutMe: profile.aboutMe || '',
          hivPositive: profile.hivPositive || false,
          religion: profile.religion || '',
          community: profile.community || profile.caste || '',
          subCommunity: profile.subCommunity || '',
          motherTongue: profile.motherTongue || '',
          education: {
            highestDegree: profile.education?.highestDegree || '',
            ugDegree: profile.education?.ugDegree || '',
            ugCollege: profile.education?.ugCollege || '',
            schoolName: profile.education?.schoolName || '',
            aboutEducation: profile.education?.aboutEducation || '',
          },
          career: {
            employedIn: profile.career?.employedIn || '',
            occupation: profile.career?.occupation || '',
            organizationName: profile.career?.organizationName || '',
            interestedInSettlingAbroad: profile.career?.interestedInSettlingAbroad || '',
            thoughtsOnSettlingAbroad: profile.career?.thoughtsOnSettlingAbroad || '',
          },
          occupation: profile.occupation || '',
          employer: profile.employer || '',
          annualIncome: { amount: profile.annualIncome?.amount || '', currency: profile.annualIncome?.currency || 'INR', period: profile.annualIncome?.period || 'Yearly' },
          familyDetails: {
            familyStatus: profile.familyDetails?.familyStatus || '',
            familyType: profile.familyDetails?.familyType || '',
            familyValues: profile.familyDetails?.familyValues || '',
            livingWithParents: profile.familyDetails?.livingWithParents || '',
            familyIncome: profile.familyDetails?.familyIncome || '',
            fatherOccupation: profile.familyDetails?.fatherOccupation || '',
            motherOccupation: profile.familyDetails?.motherOccupation || '',
            brothers: { total: profile.familyDetails?.brothers?.total ?? '', married: profile.familyDetails?.brothers?.married ?? '' },
            sisters: { total: profile.familyDetails?.sisters?.total ?? '', married: profile.familyDetails?.sisters?.married ?? '' },
            familyBasedOutOf: profile.familyDetails?.familyBasedOutOf || '',
            aboutFamily: profile.familyDetails?.aboutFamily || '',
          },
          contact: {
            email: profile.contact?.email || user.email || '',
            alternateEmail: profile.contact?.alternateEmail || '',
            phoneNumber: profile.contact?.phoneNumber || user.phone || '',
            phoneNumberOwnedBy: profile.contact?.phoneNumberOwnedBy || 'Self',
            alternateNumber: profile.contact?.alternateNumber || '',
          },
          kundliDetails: {
            manglikStatus: profile.kundliDetails?.manglikStatus || "Don't Know",
            nakshatra: profile.kundliDetails?.nakshatra || '',
            timeOfBirth: profile.kundliDetails?.timeOfBirth || '',
            placeOfBirth: profile.kundliDetails?.placeOfBirth || '',
            thoughtsOnHoroscope: profile.kundliDetails?.thoughtsOnHoroscope || '',
          },
          lifestyle: {
            diet: profile.lifestyle?.diet || '',
            drinking: profile.lifestyle?.drinking || '',
            smoking: profile.lifestyle?.smoking || '',
            assets: {
              ownHouse: profile.lifestyle?.assets?.ownHouse || false,
              ownCar: profile.lifestyle?.assets?.ownCar || false,
              description: profile.lifestyle?.assets?.description || '',
            },
            interests: {
              hobbies: profile.lifestyle?.interests?.hobbies || [],
              languagesSpoken: profile.lifestyle?.interests?.languagesSpoken || [],
              foodICook: profile.lifestyle?.interests?.foodICook || '',
              dressStyle: profile.lifestyle?.interests?.dressStyle || '',
              cuisinePreferences: profile.lifestyle?.interests?.cuisinePreferences || [],
              favouriteMusic: profile.lifestyle?.interests?.favouriteMusic || [],
              sports: profile.lifestyle?.interests?.sports || [],
              favouriteBooks: profile.lifestyle?.interests?.favouriteBooks || [],
              movies: profile.lifestyle?.interests?.movies || [],
              tvShows: profile.lifestyle?.interests?.tvShows || [],
              vacationDestination: profile.lifestyle?.interests?.vacationDestination || [],
            },
          },
          locationDetails: {
            country: profile.locationDetails?.country || 'India',
            state: profile.locationDetails?.state || '',
            city: profile.locationDetails?.city || '',
            citizenshipStatus: profile.locationDetails?.citizenshipStatus || '',
          },
          partnerPreferences: {
            ageRange: {
              min: profile.partnerPreferences?.ageRange?.min ?? 18,
              max: profile.partnerPreferences?.ageRange?.max ?? 40,
            },
            heightRange: {
              min: profile.partnerPreferences?.heightRange?.min ?? 150,
              max: profile.partnerPreferences?.heightRange?.max ?? 190,
            },
            maritalStatus: profile.partnerPreferences?.maritalStatus || [],
            religions: profile.partnerPreferences?.religions || [],
            motherTongues: profile.partnerPreferences?.motherTongues || [],
            educationLevel: profile.partnerPreferences?.educationLevel || [],
            occupations: profile.partnerPreferences?.occupations || [],
            incomeRange: {
              min: profile.partnerPreferences?.incomeRange?.min ?? '',
              max: profile.partnerPreferences?.incomeRange?.max ?? '',
            },
            locationPreferences: {
              countries: profile.partnerPreferences?.locationPreferences?.countries || [],
              states: profile.partnerPreferences?.locationPreferences?.states || [],
              cities: profile.partnerPreferences?.locationPreferences?.cities || [],
            },
            dietPreferences: profile.partnerPreferences?.dietPreferences || [],
            drinkingHabits: profile.partnerPreferences?.drinkingHabits || 'Any',
            smokingHabits: profile.partnerPreferences?.smokingHabits || 'Any',
            aboutPartner: profile.partnerPreferences?.aboutPartner || '',
          },
        });
        setPhotos(profile.photos || []);
      } else {
        setPhotos([]);
      }
    } catch {
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  // Generic per-section save
  const sectionSave = async (userPayload = {}, profilePayload = {}) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await adminService.updateUser(userId, { user: userPayload, profile: profilePayload });
      setSuccess('Saved successfully');
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Per-section save handlers ──────────────────────────────────────────────

  const handleSaveAccount = async () => {
    if (!userData.name?.trim()) { setError('Name is required'); return; }
    if (!userData.email?.trim()) { setError('Email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) { setError('Invalid email address'); return; }
    if (!userData.gender) { setError('Gender is required'); return; }
    const payload = { ...userData };
    if (newPassword.trim().length >= 8) payload.password = newPassword.trim();
    await sectionSave(payload, {});
  };

  const handleSaveBasic = async () => {
    if (profileData.dateOfBirth) {
      const age = Math.floor((Date.now() - new Date(profileData.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000));
      if (isNaN(age) || age < 18 || age > 80) {
        setError('Date of birth must result in an age between 18 and 80 years'); return;
      }
    }
    await sectionSave({}, {
      showNameToAll: profileData.showNameToAll,
      dateOfBirth: profileData.dateOfBirth || undefined,
      maritalStatus: profileData.maritalStatus,
      height: profileData.height ? Number(profileData.height) : undefined,
      weight: profileData.weight || undefined,
      profileManagedBy: profileData.profileManagedBy,
      disability: profileData.disability,
      thalassemia: profileData.thalassemia,
      income: profileData.income,
      gothra: profileData.gothra || undefined,
      currentLocation: profileData.currentLocation || undefined,
      visibility: profileData.visibility,
    });
  };

  const handleSaveAboutMe = async () => {
    await sectionSave({}, {
      aboutMe: profileData.aboutMe || undefined,
      profileManagedBy: profileData.profileManagedBy,
      religion: profileData.religion || undefined,
      community: profileData.community || undefined,
      subCommunity: profileData.subCommunity || undefined,
      motherTongue: profileData.motherTongue || undefined,
      gothra: profileData.gothra || undefined,
      disability: profileData.disability,
      thalassemia: profileData.thalassemia,
      hivPositive: profileData.hivPositive,
    });
  };

  const handleSaveEducation = async () => {
    await sectionSave({}, { education: profileData.education });
  };

  const handleSaveCareer = async () => {
    await sectionSave({}, {
      career: profileData.career,
      occupation: profileData.career?.occupation || profileData.occupation || undefined,
      employer: profileData.career?.organizationName || profileData.employer || undefined,
      annualIncome: profileData.annualIncome,
    });
  };

  const handleSaveFamily = async () => {
    const { brothers, sisters } = profileData.familyDetails;
    if (brothers.total !== '' && brothers.married !== '' && Number(brothers.married) > Number(brothers.total)) {
      setError('Married brothers cannot exceed total brothers'); return;
    }
    if (sisters.total !== '' && sisters.married !== '' && Number(sisters.married) > Number(sisters.total)) {
      setError('Married sisters cannot exceed total sisters'); return;
    }
    await sectionSave({}, { familyDetails: profileData.familyDetails });
  };

  const handleSaveContact = async () => {
    const { email, phoneNumber } = profileData.contact;
    if (!email) { setError('Contact email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Invalid contact email address'); return; }
    if (!phoneNumber) { setError('Phone number is required'); return; }
    await sectionSave({}, { contact: profileData.contact });
  };

  const handleSaveKundli = async () => {
    await sectionSave({}, { kundliDetails: profileData.kundliDetails });
  };

  const handleSaveLifestyle = async () => {
    await sectionSave({}, { lifestyle: profileData.lifestyle });
  };

  const handleSaveLocation = async () => {
    await sectionSave({}, { locationDetails: profileData.locationDetails });
  };

  const handleSavePartnerPreferences = async () => {
    const { ageRange, heightRange } = profileData.partnerPreferences;
    if (ageRange.min !== '' && ageRange.max !== '' && Number(ageRange.min) > Number(ageRange.max)) {
      setError('Minimum preferred age cannot exceed maximum preferred age'); return;
    }
    if (heightRange.min !== '' && heightRange.max !== '' && Number(heightRange.min) > Number(heightRange.max)) {
      setError('Minimum preferred height cannot exceed maximum preferred height'); return;
    }
    await sectionSave({}, { partnerPreferences: profileData.partnerPreferences });
  };

  // ── Photo handlers ────────────────────────────────────────────────────────

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await adminService.uploadUserPhoto(userId, formData);
      setPhotos((prev) => [...prev, res.data.photo]);
      setSuccess('Photo uploaded');
    } catch (err) {
      setError(err?.response?.data?.message || 'Photo upload failed');
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await adminService.deleteUserPhoto(userId, photoId);
      setPhotos((prev) => prev.filter((p) => p._id !== photoId));
      setSuccess('Photo deleted');
    } catch {
      setError('Failed to delete photo');
    }
  };

  const handleSetProfilePic = async (photoId) => {
    try {
      await adminService.setUserProfilePic(userId, photoId);
      setPhotos((prev) => prev.map((p) => ({ ...p, isProfilePic: p._id === photoId })));
      setSuccess('Profile picture updated');
    } catch {
      setError('Failed to update profile picture');
    }
  };

  const handleClose = () => {
    setTab(0);
    setError('');
    setSuccess('');
    setNewPassword('');
    setShowPassword(false);
    onClose();
  };

  // Derived: available cities for Location tab
  const availableCities = (() => {
    const { country, state } = profileData.locationDetails;
    if (country === 'India' && state) return DISTRICTS_BY_STATE[state] || [];
    if (country && country !== 'India') return CITIES_BY_COUNTRY[country] || [];
    return [];
  })();

  // Helpers for profile nested updates
  const setPD = (patch) => setProfileData((p) => ({ ...p, ...patch }));
  const setFamily = (patch) => setProfileData((p) => ({ ...p, familyDetails: { ...p.familyDetails, ...patch } }));
  const setContact = (patch) => setProfileData((p) => ({ ...p, contact: { ...p.contact, ...patch } }));
  const setKundli = (patch) => setProfileData((p) => ({ ...p, kundliDetails: { ...p.kundliDetails, ...patch } }));
  const setEdu = (patch) => setProfileData((p) => ({ ...p, education: { ...p.education, ...patch } }));
  const setCareer = (patch) => setProfileData((p) => ({ ...p, career: { ...p.career, ...patch } }));
  const setLS = (patch) => setProfileData((p) => ({ ...p, lifestyle: { ...p.lifestyle, ...patch } }));
  const setAssets = (patch) => setProfileData((p) => ({ ...p, lifestyle: { ...p.lifestyle, assets: { ...p.lifestyle.assets, ...patch } } }));
  const setInterests = (patch) => setProfileData((p) => ({ ...p, lifestyle: { ...p.lifestyle, interests: { ...p.lifestyle.interests, ...patch } } }));
  const setLoc = (patch) => setProfileData((p) => ({ ...p, locationDetails: { ...p.locationDetails, ...patch } }));
  const setPP = (patch) => setProfileData((p) => ({ ...p, partnerPreferences: { ...p.partnerPreferences, ...patch } }));
  const setPPLoc = (patch) => setProfileData((p) => ({
    ...p,
    partnerPreferences: { ...p.partnerPreferences, locationPreferences: { ...p.partnerPreferences.locationPreferences, ...patch } },
  }));

  const tabs = [
    { label: 'ACCOUNT', icon: <ManageAccounts /> },
    { label: 'BASIC DETAILS', icon: <Person /> },
    { label: 'ABOUT ME', icon: <Description /> },
    { label: 'EDUCATION', icon: <School /> },
    { label: 'CAREER', icon: <Work /> },
    { label: 'FAMILY', icon: <People /> },
    { label: 'CONTACT', icon: <Phone /> },
    { label: 'KUNDLI', icon: <AutoAwesome /> },
    { label: 'LIFESTYLE', icon: <Favorite /> },
    { label: 'LOCATION', icon: <LocationOn /> },
    { label: 'PARTNER PREFERENCES', icon: <FavoriteBorder /> },
    { label: 'PHOTOS', icon: <PhotoCamera /> },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">Edit User</Typography>
        <IconButton onClick={handleClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mx: 2, mt: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

            <Tabs
              value={tab}
              onChange={(_, v) => { setError(''); setSuccess(''); setTab(v); }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              {tabs.map((t, i) => (
                <Tab key={i} icon={t.icon} label={t.label} iconPosition="start"
                  sx={{ minHeight: 48, fontSize: '0.7rem', fontWeight: 600 }} />
              ))}
            </Tabs>

            {/* ── Tab 0: Account ──────────────────────────────────────── */}
            <TabPanel value={tab} index={0}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Account Information" description="Manage this user's login credentials and account settings.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Full Name" required value={userData.name}
                        onChange={(e) => setUserData({ ...userData, name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Email Address" required value={userData.email}
                        onChange={(e) => setUserData({ ...userData, email: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Phone Number" value={userData.phone}
                        onChange={(e) => setUserData({ ...userData, phone: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Gender" required value={userData.gender}
                        onChange={(e) => setUserData({ ...userData, gender: e.target.value })}>
                        <MenuItem value=""><em>Select Gender</em></MenuItem>
                        {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Profile For" value={userData.profileFor}
                        onChange={(e) => setUserData({ ...userData, profileFor: e.target.value })}>
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {PROFILE_FOR.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Looking For" value={userData.lookingFor}
                        onChange={(e) => setUserData({ ...userData, lookingFor: e.target.value })}>
                        {LOOKING_FOR.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Membership & Verification" description="Control membership type and verification status.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="Membership Type" value={userData.membershipType}
                        onChange={(e) => setUserData({ ...userData, membershipType: e.target.value })}>
                        {MEMBERSHIP_TYPES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="Email Verified" value={userData.isEmailVerified ? 'true' : 'false'}
                        onChange={(e) => setUserData({ ...userData, isEmailVerified: e.target.value === 'true' })}>
                        <MenuItem value="true">Verified</MenuItem>
                        <MenuItem value="false">Not Verified</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="User Verified" value={userData.isVerified ? 'true' : 'false'}
                        onChange={(e) => setUserData({ ...userData, isVerified: e.target.value === 'true' })}>
                        <MenuItem value="true">Verified</MenuItem>
                        <MenuItem value="false">Not Verified</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Profile Visibility" value={profileData.visibility}
                        onChange={(e) => setPD({ visibility: e.target.value })}>
                        <MenuItem value="all">Visible to All</MenuItem>
                        <MenuItem value="paid-only">Paid Members Only</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Reset Password" description="Set a new password for this user. Leave blank to keep the current password.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="New Password"
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        helperText="Minimum 8 characters. Leave blank to keep current password."
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveAccount} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 1: Basic Details ────────────────────────────────── */}
            <TabPanel value={tab} index={1}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Personal Details" description="Basic information about the user.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="date" label="Date of Birth" InputLabelProps={{ shrink: true }}
                        value={profileData.dateOfBirth}
                        onChange={(e) => setPD({ dateOfBirth: e.target.value })}
                        helperText="User must be between 18 and 80 years old" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Marital Status" value={profileData.maritalStatus}
                        onChange={(e) => setPD({ maritalStatus: e.target.value })}>
                        {MARITAL_STATUS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Height (cm)" value={profileData.height || ''}
                        onChange={(e) => setPD({ height: Number(e.target.value) })}>
                        <MenuItem value="">Not Specified</MenuItem>
                        {HEIGHT_OPTIONS.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h} cm ({Math.floor(h / 30.48)} ft {Math.round((h % 30.48) / 2.54)} in)
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="Weight (kg)" value={profileData.weight}
                        onChange={(e) => setPD({ weight: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Current Location" value={profileData.currentLocation}
                        onChange={(e) => setPD({ currentLocation: e.target.value })}
                        placeholder="e.g., Delhi, Mumbai" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Gothra" value={profileData.gothra}
                        onChange={(e) => setPD({ gothra: e.target.value })}
                        placeholder="Enter gothra (if applicable)" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox checked={profileData.showNameToAll}
                            onChange={(e) => setPD({ showNameToAll: e.target.checked })} />
                        }
                        label="Show name to all members" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Profile Management & Health">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Profile Managed By" value={profileData.profileManagedBy}
                        onChange={(e) => setPD({ profileManagedBy: e.target.value })}>
                        {PROFILE_MANAGED_BY.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Disability" value={profileData.disability}
                        onChange={(e) => setPD({ disability: e.target.value })}>
                        {DISABILITY.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Thalassemia" value={profileData.thalassemia}
                        onChange={(e) => setPD({ thalassemia: e.target.value })}>
                        {THALASSEMIA.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Income">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Income Amount" value={profileData.income.amount}
                        onChange={(e) => setPD({ income: { ...profileData.income, amount: e.target.value } })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Currency" value={profileData.income.currency}
                        onChange={(e) => setPD({ income: { ...profileData.income, currency: e.target.value } })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="Period" value={profileData.income.period}
                        onChange={(e) => setPD({ income: { ...profileData.income, period: e.target.value } })}>
                        {INCOME_PERIOD.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveBasic} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 2: About Me ─────────────────────────────────────── */}
            <TabPanel value={tab} index={2}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="About Me" description="Describe yourself in a few words to help others know you better.">
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={4} label="About Me" value={profileData.aboutMe}
                        onChange={(e) => setPD({ aboutMe: e.target.value })}
                        inputProps={{ maxLength: 255 }}
                        helperText={`${profileData.aboutMe.length}/255 characters`}
                        placeholder="Tell us about yourself, interests, hobbies, etc." />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Profile Managed By" value={profileData.profileManagedBy}
                        onChange={(e) => setPD({ profileManagedBy: e.target.value })}>
                        {PROFILE_MANAGED_BY.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Religion & Community">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Religion" value={profileData.religion}
                        onChange={(e) => setPD({ religion: e.target.value, community: '' })}>
                        <MenuItem value=""><em>Select Religion</em></MenuItem>
                        {RELIGIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Caste / Community" value={profileData.community}
                        onChange={(e) => setPD({ community: e.target.value })}
                        disabled={!profileData.religion}>
                        <MenuItem value=""><em>Select Caste / Community</em></MenuItem>
                        {(CASTE_BY_RELIGION[profileData.religion] || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Sub Community" value={profileData.subCommunity}
                        onChange={(e) => setPD({ subCommunity: e.target.value })}
                        placeholder="e.g. Iyer, Iyengar, Deshastha…" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Mother Tongue" value={profileData.motherTongue}
                        onChange={(e) => setPD({ motherTongue: e.target.value })}>
                        <MenuItem value=""><em>Select Mother Tongue</em></MenuItem>
                        {MOTHER_TONGUES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Gothra" value={profileData.gothra}
                        onChange={(e) => setPD({ gothra: e.target.value })}
                        placeholder="Enter your gothra (if applicable)" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Health Information">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Disability" value={profileData.disability}
                        onChange={(e) => setPD({ disability: e.target.value })}>
                        {DISABILITY.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Thalassemia" value={profileData.thalassemia}
                        onChange={(e) => setPD({ thalassemia: e.target.value })}>
                        {THALASSEMIA.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox checked={profileData.hivPositive}
                          onChange={(e) => setPD({ hivPositive: e.target.checked })} />}
                        label="HIV Positive" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveAboutMe} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 3: Education ────────────────────────────────────── */}
            <TabPanel value={tab} index={3}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Education Information" description="Details about the user's educational background.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Highest Degree" value={profileData.education.highestDegree}
                        onChange={(e) => setEdu({ highestDegree: e.target.value })}>
                        <MenuItem value=""><em>Select Degree</em></MenuItem>
                        {DEGREE_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="UG Degree" value={profileData.education.ugDegree}
                        onChange={(e) => setEdu({ ugDegree: e.target.value })}
                        placeholder="e.g., B.Tech, BBA, B.Sc" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="UG College" value={profileData.education.ugCollege}
                        onChange={(e) => setEdu({ ugCollege: e.target.value })}
                        placeholder="e.g., IIT Delhi, Delhi University" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="School Name" value={profileData.education.schoolName}
                        onChange={(e) => setEdu({ schoolName: e.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={3} label="About My Education" value={profileData.education.aboutEducation}
                        onChange={(e) => setEdu({ aboutEducation: e.target.value })}
                        placeholder="Tell us more about your educational background…" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveEducation} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 4: Career ───────────────────────────────────────── */}
            <TabPanel value={tab} index={4}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Career Information" description="Details about the user's professional background.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Employed In" value={profileData.career.employedIn}
                        onChange={(e) => setCareer({ employedIn: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {EMPLOYED_IN.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Occupation" value={profileData.career.occupation}
                        onChange={(e) => setCareer({ occupation: e.target.value })}
                        placeholder="e.g., Software Engineer, Doctor" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Organization / Employer" value={profileData.career.organizationName}
                        onChange={(e) => { setCareer({ organizationName: e.target.value }); setPD({ employer: e.target.value }); }}
                        placeholder="e.g., Infosys, AIIMS" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Interested in Settling Abroad" value={profileData.career.interestedInSettlingAbroad}
                        onChange={(e) => setCareer({ interestedInSettlingAbroad: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {SETTLING_ABROAD.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={3} label="Thoughts on Settling Abroad" value={profileData.career.thoughtsOnSettlingAbroad}
                        onChange={(e) => setCareer({ thoughtsOnSettlingAbroad: e.target.value })} />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Annual Income">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Amount" value={profileData.annualIncome.amount}
                        onChange={(e) => setPD({ annualIncome: { ...profileData.annualIncome, amount: e.target.value } })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Currency" value={profileData.annualIncome.currency}
                        onChange={(e) => setPD({ annualIncome: { ...profileData.annualIncome, currency: e.target.value } })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="Period" value={profileData.annualIncome.period}
                        onChange={(e) => setPD({ annualIncome: { ...profileData.annualIncome, period: e.target.value } })}>
                        {INCOME_PERIOD.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveCareer} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 5: Family ───────────────────────────────────────── */}
            <TabPanel value={tab} index={5}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Family Background" description="Information about the user's family.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Family Status" value={profileData.familyDetails.familyStatus}
                        onChange={(e) => setFamily({ familyStatus: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {FAMILY_STATUS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Family Type" value={profileData.familyDetails.familyType}
                        onChange={(e) => setFamily({ familyType: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {FAMILY_TYPE.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Family Values" value={profileData.familyDetails.familyValues}
                        onChange={(e) => setFamily({ familyValues: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {FAMILY_VALUES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Living With Parents" value={profileData.familyDetails.livingWithParents}
                        onChange={(e) => setFamily({ livingWithParents: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {LIVING_WITH_PARENTS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Family Income" value={profileData.familyDetails.familyIncome}
                        onChange={(e) => setFamily({ familyIncome: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Family Based Out Of" value={profileData.familyDetails.familyBasedOutOf}
                        onChange={(e) => setFamily({ familyBasedOutOf: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Father's Occupation" value={profileData.familyDetails.fatherOccupation}
                        onChange={(e) => setFamily({ fatherOccupation: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Mother's Occupation" value={profileData.familyDetails.motherOccupation}
                        onChange={(e) => setFamily({ motherOccupation: e.target.value })} />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Siblings">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <TextField select fullWidth label="Brothers (Total)" value={profileData.familyDetails.brothers.total}
                        onChange={(e) => setFamily({ brothers: { ...profileData.familyDetails.brothers, total: e.target.value } })}>
                        <MenuItem value="">-</MenuItem>
                        {BROTHER_SISTER_COUNT.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField select fullWidth label="Brothers (Married)" value={profileData.familyDetails.brothers.married}
                        onChange={(e) => setFamily({ brothers: { ...profileData.familyDetails.brothers, married: e.target.value } })}>
                        <MenuItem value="">-</MenuItem>
                        {BROTHER_SISTER_COUNT.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField select fullWidth label="Sisters (Total)" value={profileData.familyDetails.sisters.total}
                        onChange={(e) => setFamily({ sisters: { ...profileData.familyDetails.sisters, total: e.target.value } })}>
                        <MenuItem value="">-</MenuItem>
                        {BROTHER_SISTER_COUNT.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField select fullWidth label="Sisters (Married)" value={profileData.familyDetails.sisters.married}
                        onChange={(e) => setFamily({ sisters: { ...profileData.familyDetails.sisters, married: e.target.value } })}>
                        <MenuItem value="">-</MenuItem>
                        {BROTHER_SISTER_COUNT.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={3} label="About Family" value={profileData.familyDetails.aboutFamily}
                        onChange={(e) => setFamily({ aboutFamily: e.target.value })}
                        placeholder="Tell us more about your family…" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveFamily} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 6: Contact ──────────────────────────────────────── */}
            <TabPanel value={tab} index={6}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Contact Information" description="These details will help other profiles get in touch.">
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField fullWidth required label="Email ID" value={profileData.contact.email}
                        onChange={(e) => setContact({ email: e.target.value })}
                        helperText="This email will be used for communication" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Alternate Email ID (Optional)" value={profileData.contact.alternateEmail}
                        onChange={(e) => setContact({ alternateEmail: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required label="Phone Number" value={profileData.contact.phoneNumber}
                        onChange={(e) => setContact({ phoneNumber: e.target.value })}
                        helperText="This phone number will be used for communication"
                        placeholder="e.g. +91 9876543210" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Phone Number Owned By" value={profileData.contact.phoneNumberOwnedBy}
                        onChange={(e) => setContact({ phoneNumberOwnedBy: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {PHONE_OWNED_BY.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Alternate Phone Number (Optional)" value={profileData.contact.alternateNumber}
                        onChange={(e) => setContact({ alternateNumber: e.target.value })}
                        placeholder="e.g. +91 9876543210" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveContact} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 7: Kundli ───────────────────────────────────────── */}
            <TabPanel value={tab} index={7}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Kundli and Astro Details" description="These details help increase chances of compatibility.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Manglik Status" value={profileData.kundliDetails.manglikStatus}
                        onChange={(e) => setKundli({ manglikStatus: e.target.value })}>
                        {MANGLIK.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Nakshatra" value={profileData.kundliDetails.nakshatra}
                        onChange={(e) => setKundli({ nakshatra: e.target.value })}
                        placeholder="e.g., Rohini, Ashwini, etc." />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="time" label="Time of Birth" InputLabelProps={{ shrink: true }}
                        value={profileData.kundliDetails.timeOfBirth}
                        onChange={(e) => setKundli({ timeOfBirth: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Place of Birth" value={profileData.kundliDetails.placeOfBirth}
                        onChange={(e) => setKundli({ placeOfBirth: e.target.value })}
                        placeholder="e.g., Delhi, Mumbai, etc." />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={4} label="Thoughts on Horoscope" value={profileData.kundliDetails.thoughtsOnHoroscope}
                        onChange={(e) => setKundli({ thoughtsOnHoroscope: e.target.value })}
                        placeholder="Share thoughts about horoscope matching…" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveKundli} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 8: Lifestyle ────────────────────────────────────── */}
            <TabPanel value={tab} index={8}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Lifestyle Habits" description="Details about diet and social habits.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="Diet" value={profileData.lifestyle.diet}
                        onChange={(e) => setLS({ diet: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {DIET.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="Drinking" value={profileData.lifestyle.drinking}
                        onChange={(e) => setLS({ drinking: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {DRINKING_SMOKING.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth label="Smoking" value={profileData.lifestyle.smoking}
                        onChange={(e) => setLS({ smoking: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {DRINKING_SMOKING.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Assets">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={<Checkbox checked={profileData.lifestyle.assets.ownHouse}
                          onChange={(e) => setAssets({ ownHouse: e.target.checked })} />}
                        label="Own House" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={<Checkbox checked={profileData.lifestyle.assets.ownCar}
                          onChange={(e) => setAssets({ ownCar: e.target.checked })} />}
                        label="Own Car" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Assets Description" value={profileData.lifestyle.assets.description}
                        onChange={(e) => setAssets({ description: e.target.value })} />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Interests & Favourites">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={HOBBIES_OPTIONS}
                        value={profileData.lifestyle.interests.hobbies}
                        onChange={(_, v) => setInterests({ hobbies: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Hobbies" placeholder="Add hobby" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={LANGUAGE_OPTIONS}
                        value={profileData.lifestyle.interests.languagesSpoken}
                        onChange={(_, v) => setInterests({ languagesSpoken: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Languages Spoken" placeholder="Add language" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={CUISINE_OPTIONS}
                        value={profileData.lifestyle.interests.cuisinePreferences}
                        onChange={(_, v) => setInterests({ cuisinePreferences: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Cuisine Preferences" placeholder="Add cuisine" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={MUSIC_OPTIONS}
                        value={profileData.lifestyle.interests.favouriteMusic}
                        onChange={(_, v) => setInterests({ favouriteMusic: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Favourite Music" placeholder="Add genre" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={SPORTS_OPTIONS}
                        value={profileData.lifestyle.interests.sports}
                        onChange={(_, v) => setInterests({ sports: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Sports" placeholder="Add sport" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Dress Style" value={profileData.lifestyle.interests.dressStyle}
                        onChange={(e) => setInterests({ dressStyle: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {DRESS_STYLES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Food I Cook" value={profileData.lifestyle.interests.foodICook}
                        onChange={(e) => setInterests({ foodICook: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={[]}
                        value={profileData.lifestyle.interests.vacationDestination}
                        onChange={(_, v) => setInterests({ vacationDestination: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Vacation Destinations" placeholder="Add destination" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={[]}
                        value={profileData.lifestyle.interests.movies}
                        onChange={(_, v) => setInterests({ movies: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Favourite Movies" placeholder="Add movie" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={[]}
                        value={profileData.lifestyle.interests.tvShows}
                        onChange={(_, v) => setInterests({ tvShows: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Favourite TV Shows" placeholder="Add show" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple freeSolo options={[]}
                        value={profileData.lifestyle.interests.favouriteBooks}
                        onChange={(_, v) => setInterests({ favouriteBooks: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Favourite Books" placeholder="Add book" />}
                      />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveLifestyle} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 9: Location ─────────────────────────────────────── */}
            <TabPanel value={tab} index={9}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Location Information" description="Current location and residency details.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Country *" value={profileData.locationDetails.country}
                        onChange={(e) => {
                          const country = e.target.value;
                          setLoc({ country, state: '', city: '' });
                        }}>
                        {COUNTRIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </TextField>
                    </Grid>
                    {profileData.locationDetails.country === 'India' && (
                      <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="State" value={profileData.locationDetails.state}
                          onChange={(e) => setLoc({ state: e.target.value, city: '' })}>
                          <MenuItem value=""><em>Select State</em></MenuItem>
                          {INDIAN_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label={profileData.locationDetails.country === 'India' ? 'District' : 'City'} value={profileData.locationDetails.city}
                        onChange={(e) => setLoc({ city: e.target.value })}
                        disabled={availableCities.length === 0}>
                        <MenuItem value=""><em>{profileData.locationDetails.country === 'India' ? 'Select District' : 'Select City'}</em></MenuItem>
                        {availableCities.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Citizenship Status" value={profileData.locationDetails.citizenshipStatus}
                        onChange={(e) => setLoc({ citizenshipStatus: e.target.value })}>
                        <MenuItem value=""><em>Not Specified</em></MenuItem>
                        {CITIZENSHIP.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Current Location (Free Text)" value={profileData.currentLocation}
                        onChange={(e) => setPD({ currentLocation: e.target.value })}
                        placeholder="e.g., Sector 15, Noida" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSaveLocation} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 10: Partner Preferences ─────────────────────────── */}
            <TabPanel value={tab} index={10}>
              <Box sx={{ px: 3, pb: 2 }}>
                <SectionPaper title="Age & Height" description="Preferred age and height range for a partner.">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth type="number" label="Min Age" value={profileData.partnerPreferences.ageRange.min}
                        onChange={(e) => setPP({ ageRange: { ...profileData.partnerPreferences.ageRange, min: e.target.value === '' ? '' : Number(e.target.value) } })} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth type="number" label="Max Age" value={profileData.partnerPreferences.ageRange.max}
                        onChange={(e) => setPP({ ageRange: { ...profileData.partnerPreferences.ageRange, max: e.target.value === '' ? '' : Number(e.target.value) } })} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField select fullWidth label="Min Height (cm)" value={profileData.partnerPreferences.heightRange.min}
                        onChange={(e) => setPP({ heightRange: { ...profileData.partnerPreferences.heightRange, min: Number(e.target.value) } })}>
                        {HEIGHT_OPTIONS.map((h) => <MenuItem key={h} value={h}>{h} cm</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField select fullWidth label="Max Height (cm)" value={profileData.partnerPreferences.heightRange.max}
                        onChange={(e) => setPP({ heightRange: { ...profileData.partnerPreferences.heightRange, max: Number(e.target.value) } })}>
                        {HEIGHT_OPTIONS.map((h) => <MenuItem key={h} value={h}>{h} cm</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Marital Status & Background">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple options={PARTNER_MARITAL_STATUS}
                        value={profileData.partnerPreferences.maritalStatus}
                        onChange={(_, v) => setPP({ maritalStatus: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Acceptable Marital Status" placeholder="Select status" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple options={RELIGIONS}
                        value={profileData.partnerPreferences.religions}
                        onChange={(_, v) => setPP({ religions: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Preferred Religions" placeholder="Select religions" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple options={MOTHER_TONGUES}
                        value={profileData.partnerPreferences.motherTongues}
                        onChange={(_, v) => setPP({ motherTongues: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Preferred Mother Tongues" placeholder="Select mother tongues" />}
                      />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Education, Career & Income">
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple options={PARTNER_EDUCATION_LEVELS}
                        value={profileData.partnerPreferences.educationLevel}
                        onChange={(_, v) => setPP({ educationLevel: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Preferred Education Levels" placeholder="Select education levels" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete multiple options={PARTNER_OCCUPATIONS}
                        value={profileData.partnerPreferences.occupations}
                        onChange={(_, v) => setPP({ occupations: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Preferred Occupations" placeholder="Select occupations" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Minimum Income" value={profileData.partnerPreferences.incomeRange.min}
                        onChange={(e) => setPP({ incomeRange: { ...profileData.partnerPreferences.incomeRange, min: e.target.value } })}
                        placeholder="e.g., 500000" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Maximum Income (leave empty for 'and above')" value={profileData.partnerPreferences.incomeRange.max}
                        onChange={(e) => setPP({ incomeRange: { ...profileData.partnerPreferences.incomeRange, max: e.target.value } })}
                        placeholder="e.g., 1000000" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Location Preferences">
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Autocomplete multiple options={COUNTRIES}
                        value={profileData.partnerPreferences.locationPreferences.countries}
                        onChange={(_, v) => setPPLoc({ countries: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Preferred Countries" placeholder="Select countries" />}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete multiple options={PARTNER_INDIAN_STATES}
                        value={profileData.partnerPreferences.locationPreferences.states}
                        onChange={(_, v) => setPPLoc({ states: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Preferred States/Regions" placeholder="Select states/regions" />}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Preferred Cities (comma separated)"
                        value={profileData.partnerPreferences.locationPreferences.cities.join(', ')}
                        onChange={(e) => setPPLoc({ cities: e.target.value.split(',').map((c) => c.trim()).filter(Boolean) })}
                        placeholder="e.g., Mumbai, Delhi, Bangalore" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="Lifestyle Preferences">
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Autocomplete multiple options={PARTNER_DIET_OPTIONS}
                        value={profileData.partnerPreferences.dietPreferences}
                        onChange={(_, v) => setPP({ dietPreferences: v })}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip label={option} {...getTagProps({ index })} key={index} />
                        ))}
                        renderInput={(params) => <TextField {...params} label="Dietary Habits" placeholder="Select dietary habits" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Drinking Habits" value={profileData.partnerPreferences.drinkingHabits}
                        onChange={(e) => setPP({ drinkingHabits: e.target.value })}>
                        {PARTNER_DRINKING_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Smoking Habits" value={profileData.partnerPreferences.smokingHabits}
                        onChange={(e) => setPP({ smokingHabits: e.target.value })}>
                        {PARTNER_SMOKING_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SectionPaper title="About Ideal Partner">
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={4} label="Describe Ideal Partner" value={profileData.partnerPreferences.aboutPartner}
                        onChange={(e) => setPP({ aboutPartner: e.target.value })}
                        placeholder="What qualities are they looking for in a partner?" />
                    </Grid>
                  </Grid>
                </SectionPaper>

                <SaveButton onClick={handleSavePartnerPreferences} saving={saving} />
              </Box>
            </TabPanel>

            {/* ── Tab 11: Photos ──────────────────────────────────────── */}
            <TabPanel value={tab} index={11}>
              <Box sx={{ px: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {photos.length} photo(s) — click ★ to set as profile picture
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={photoUploading ? <CircularProgress size={16} /> : <AddPhotoAlternate />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    Upload Photo
                  </Button>
                  <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handlePhotoUpload} />
                </Box>

                {photos.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                    <CloudUpload sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No photos yet. Upload one above.</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {photos.map((photo) => (
                      <Grid item xs={6} sm={4} md={3} key={photo._id}>
                        <Box sx={{
                          position: 'relative', borderRadius: 2, overflow: 'hidden',
                          border: photo.isProfilePic ? '3px solid' : '1px solid',
                          borderColor: photo.isProfilePic ? 'primary.main' : 'divider',
                        }}>
                          <img src={photo.url} alt="user"
                            style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block' }} />
                          {photo.isProfilePic && (
                            <Chip label="Profile Pic" size="small" color="primary"
                              sx={{ position: 'absolute', top: 4, left: 4, fontSize: 10 }} />
                          )}
                          <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Tooltip title={photo.isProfilePic ? 'Already profile pic' : 'Set as profile pic'}>
                              <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                                onClick={() => handleSetProfilePic(photo._id)} disabled={photo.isProfilePic}>
                                {photo.isProfilePic ? <Star fontSize="small" color="primary" /> : <StarBorder fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete photo">
                              <IconButton size="small" sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                                onClick={() => handleDeletePhoto(photo._id)}>
                                <Delete fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Each section saves independently — use the Save Changes button inside each tab.
        </Typography>
        <Button onClick={handleClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
